import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Video, Loader2, Play, RefreshCw, Package, ArchiveX, ExternalLink,
  DollarSign, Users, Clock,
} from "lucide-react";
import SidebarLayout from "@/components/SidebarLayout";
import AdminVideosV2Packs from "@/components/videos/AdminVideosV2Packs";
import AdminVideosQueueMonitor from "@/components/videos/AdminVideosQueueMonitor";

const API = process.env.REACT_APP_BACKEND_URL || "";

async function api(path, options = {}) {
  const r = await fetch(`${API}/api${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  const text = await r.text();
  let data = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = { raw: text }; }
  }
  if (!r.ok) {
    const err = new Error(data?.detail?.error || data?.detail || `http_${r.status}`);
    err.status = r.status;
    err.data = data;
    throw err;
  }
  return data;
}

const TABS = [
  { key: "videos", label: "Videos" },
  { key: "packs", label: "Packs" },
  { key: "counters", label: "Counters" },
  { key: "v2_packs", label: "Packs" },
  { key: "queue_monitor", label: "Queue Monitor" },
];

/**
 * Phase 26 · Admin panel for Video Engine.
 * Route: /admin/videos
 */
export default function AdminVideos() {
  const [tab, setTab] = useState("videos");
  const [flags, setFlags] = useState({ videoEngine: null });
  const [countersToday, setCountersToday] = useState(null);
  const [videos, setVideos] = useState([]);
  const [packs, setPacks] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadFlags = async () => {
    try {
      const r = await api("/admin/system-settings");
      const items = r?.items || r?.settings || [];
      const ve = items.find((x) => x.key === "VIDEO_ENGINE_ENABLED");
      setFlags({ videoEngine: ve?.value ?? null });
    } catch {
      setFlags({ videoEngine: null });
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, v, p] = await Promise.all([
        api("/admin/videos/counters?days=30").catch(() => null),
        api("/admin/videos?limit=50").catch(() => ({ items: [] })),
        api("/admin/videos/packs?limit=50").catch(() => ({ items: [] })),
      ]);
      setCountersToday(c?.today || null);
      setHistory(c?.history || []);
      setVideos(v?.items || []);
      setPacks(p?.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadFlags(); loadAll(); }, []);

  const setFlag = async (key, value) => {
    try {
      await api(`/admin/system-settings/${key}`, {
        method: "PATCH",
        body: JSON.stringify({ value, reason: "admin panel toggle" }),
      });
      toast.success(`${key} → ${value}`);
      loadFlags();
    } catch (e) {
      toast.error(`Toggle failed: ${e.message}`);
    }
  };

  const triggerVideoCron = async () => {
    setBusy(true);
    try {
      const r = await api("/admin/videos/cron/trigger", {
        method: "POST",
        body: JSON.stringify({ max_items: 3 }),
      });
      toast.success(`Generated ${r.generated} · Skipped ${r.skipped?.length || 0}`);
      loadAll();
    } catch (e) {
      toast.error(`Cron failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const triggerPackCron = async () => {
    setBusy(true);
    try {
      const r = await api("/admin/videos/packs/cron/trigger", {
        method: "POST",
        body: JSON.stringify({}),
      });
      toast.success(`Packs — created=${r.created || 0} updated=${r.updated || 0}`);
      loadAll();
    } catch (e) {
      toast.error(`Pack cron failed: ${e.message}`);
    } finally {
      setBusy(false);
    }
  };

  const publishPack = async (packId) => {
    const price = prompt("Price in EUR cents (e.g. 1900 = $19.00):", "1900");
    if (!price) return;
    try {
      await api(`/admin/videos/packs/${packId}/publish-as-asset`, {
        method: "POST",
        body: JSON.stringify({ price_eur_cents: parseInt(price, 10) }),
      });
      toast.success("Pack published as marketplace asset");
      loadAll();
    } catch (e) {
      toast.error(`Publish failed: ${e.message}`);
    }
  };

  const unpublishPack = async (packId) => {
    if (!window.confirm("Delist this pack from marketplace?")) return;
    try {
      await api(`/admin/videos/packs/${packId}/unpublish`, { method: "POST" });
      toast.success("Pack unpublished");
      loadAll();
    } catch (e) {
      toast.error(`Unpublish failed: ${e.message}`);
    }
  };

  const archiveVideo = async (videoId) => {
    if (!window.confirm("Archive this video (hidden from public)?")) return;
    try {
      await api(`/admin/videos/${videoId}/archive`, { method: "POST" });
      toast.success("Video archived");
      loadAll();
    } catch (e) {
      toast.error(`Archive failed: ${e.message}`);
    }
  };

  return (
    <SidebarLayout>
      <div
        className="max-w-[1200px] w-full mx-auto p-6 md:p-8 space-y-6"
        data-testid="admin-videos-page"
      >
        <motion.header
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-end justify-between flex-wrap gap-4"
        >
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-1">
              <Video className="h-3 w-3 text-[var(--color-secondary)]" />
              Admin · Phase 26 · Video Engine V1
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">
              Video Engine
            </h1>
            <p className="text-xs text-text-secondary mt-1 max-w-2xl">
              Short-form AI video generation · daily cron · monthly packs · marketplace
              publishing. Metadata + gradient thumbnail only (no real media).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-videos-refresh"
              onClick={loadAll}
              className="text-xs px-3 py-1.5 rounded-full border border-[var(--color-border)] hover:border-[var(--color-primary)] inline-flex items-center gap-1"
            >
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
          </div>
        </motion.header>

        {/* Kill switch row */}
        <section
          data-testid="admin-videos-kill-switch"
          className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 flex items-center justify-between"
        >
          <div>
            <div className="text-xs uppercase tracking-widest text-text-secondary mb-0.5">
              VIDEO_ENGINE_ENABLED
            </div>
            <div className="text-xs text-text-secondary">
              Guards cron generation & pack aggregation. Public reads stay available.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span
              data-testid="admin-videos-flag-state"
              className={`text-xs px-2 py-1 rounded-full border ${
                flags.videoEngine === true
                  ? "border-[var(--color-success)]/40 bg-[var(--color-success)]/10 text-[var(--color-success)]"
                  : "border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10 text-[var(--color-alert)]"
              }`}
            >
              {flags.videoEngine === null ? "—" : flags.videoEngine ? "ON" : "OFF"}
            </span>
            <button
              data-testid="admin-videos-toggle-flag"
              onClick={() => setFlag("VIDEO_ENGINE_ENABLED", !flags.videoEngine)}
              className="text-xs px-3 py-1.5 rounded-full bg-white hover:bg-[#EDEDED] text-black hover:opacity-90"
            >
              Toggle
            </button>
          </div>
        </section>

        {/* KPI row */}
        {countersToday && (
          <section
            data-testid="admin-videos-kpi"
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            <Kpi
              icon={<Video className="h-4 w-4" />}
              label="Videos today"
              value={countersToday.videos_generated || 0}
              hint={`/ 20 daily cap`}
            />
            <Kpi
              icon={<Package className="h-4 w-4" />}
              label="Packs today"
              value={countersToday.packs_created || 0}
            />
            <Kpi
              icon={<DollarSign className="h-4 w-4" />}
              label="LLM budget today"
              value={`$${(countersToday.llm_usage_usd || 0).toFixed(3)}`}
              hint={`/ $9 platform cap`}
            />
            <Kpi
              icon={<Clock className="h-4 w-4" />}
              label="Total videos"
              value={videos.length}
            />
          </section>
        )}

        {/* Cron triggers */}
        <section
          data-testid="admin-videos-cron-row"
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-text-secondary mb-0.5">
                Daily video cron
              </div>
              <div className="text-xs text-text-secondary">
                Runs 05:30 UTC · caps 20/day · $9/day LLM shared
              </div>
            </div>
            <button
              data-testid="admin-videos-cron-trigger"
              disabled={busy}
              onClick={triggerVideoCron}
              className="text-xs px-3 py-1.5 rounded-full bg-white hover:bg-[#EDEDED] text-black hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-1"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
              Trigger now
            </button>
          </div>
          <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-text-secondary mb-0.5">
                Monthly pack cron
              </div>
              <div className="text-xs text-text-secondary">
                Aggregates prev month · virtual creator · idempotent
              </div>
            </div>
            <button
              data-testid="admin-videos-packs-cron-trigger"
              disabled={busy}
              onClick={triggerPackCron}
              className="text-xs px-3 py-1.5 rounded-full border border-[var(--color-border)] hover:border-[var(--color-primary)] inline-flex items-center gap-1"
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Package className="h-3 w-3" />}
              Aggregate
            </button>
          </div>
        </section>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[var(--color-border)]" data-testid="admin-videos-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-videos-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`text-xs px-4 py-2 uppercase tracking-widest border-b-2 -mb-[1px] ${
                tab === t.key
                  ? "border-[var(--color-primary)] text-[var(--color-text-main)]"
                  : "border-transparent text-text-secondary hover:text-[var(--color-text-main)]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-text-secondary text-xs">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        ) : tab === "videos" ? (
          <VideosTable videos={videos} onArchive={archiveVideo} />
        ) : tab === "packs" ? (
          <PacksTable packs={packs} onPublish={publishPack} onUnpublish={unpublishPack} />
        ) : tab === "v2_packs" ? (
          <AdminVideosV2Packs />
        ) : tab === "queue_monitor" ? (
          <AdminVideosQueueMonitor />
        ) : (
          <CountersHistory history={history} />
        )}
      </div>
    </SidebarLayout>
  );
}

function Kpi({ icon, label, value, hint }) {
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-text-secondary mb-1">
        {icon} {label}
      </div>
      <div className="text-2xl font-semibold text-[var(--color-text-main)]">{value}</div>
      {hint && <div className="text-[10px] text-text-secondary mt-0.5">{hint}</div>}
    </div>
  );
}

function VideosTable({ videos, onArchive }) {
  if (videos.length === 0) {
    return (
      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-12 text-center text-sm text-text-secondary">
        No videos yet. Trigger the cron above to generate some.
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
      <table className="w-full text-xs" data-testid="admin-videos-table">
        <thead className="bg-[var(--color-bg)] text-text-secondary">
          <tr>
            <th className="text-left px-4 py-3 font-medium uppercase tracking-widest">Title</th>
            <th className="text-left px-4 py-3 font-medium uppercase tracking-widest">Creator</th>
            <th className="text-left px-4 py-3 font-medium uppercase tracking-widest">Status</th>
            <th className="text-right px-4 py-3 font-medium uppercase tracking-widest">Views</th>
            <th className="text-right px-4 py-3 font-medium uppercase tracking-widest">Shares</th>
            <th className="text-right px-4 py-3 font-medium uppercase tracking-widest">Actions</th>
          </tr>
        </thead>
        <tbody>
          {videos.map((v) => (
            <tr
              key={v.video_id}
              data-testid={`admin-videos-row-${v.video_id}`}
              className="border-t border-[var(--color-border)]"
            >
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div
                    className="h-8 w-14 rounded-md flex-shrink-0"
                    style={{
                      background: v.thumbnail_gradient
                        ? `linear-gradient(${v.thumbnail_gradient.angle || 135}deg, ${v.thumbnail_gradient.start_color}, ${v.thumbnail_gradient.end_color})`
                        : "linear-gradient(135deg, #FFFFFF, #CCCCCC)",
                    }}
                  />
                  <div className="min-w-0">
                    <div className="text-[var(--color-text-main)] truncate max-w-xs">{v.title}</div>
                    <div className="text-[10px] text-text-secondary">{v.duration_sec}s</div>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3 text-text-secondary">{v.virtual_creator_id}</td>
              <td className="px-4 py-3">
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                  v.status === "moderated_clean" || v.status === "generated"
                    ? "border-[var(--color-success)]/40 bg-[var(--color-success)]/10 text-[var(--color-success)]"
                    : v.status === "moderated_flagged"
                    ? "border-neutral-600 bg-neutral-800 text-neutral-200"
                    : "border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10 text-[var(--color-alert)]"
                }`}>
                  {v.status}
                </span>
              </td>
              <td className="px-4 py-3 text-right">{v.view_count || 0}</td>
              <td className="px-4 py-3 text-right">{v.share_count || 0}</td>
              <td className="px-4 py-3 text-right">
                <div className="inline-flex items-center gap-2">
                  <a
                    href={`/video/${v.video_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`admin-videos-open-${v.video_id}`}
                    className="text-[10px] uppercase tracking-widest text-text-secondary hover:text-[var(--color-secondary)] inline-flex items-center gap-1"
                  >
                    <ExternalLink className="h-3 w-3" /> Open
                  </a>
                  <button
                    data-testid={`admin-videos-archive-${v.video_id}`}
                    onClick={() => onArchive(v.video_id)}
                    className="text-[10px] uppercase tracking-widest text-text-secondary hover:text-[var(--color-alert)] inline-flex items-center gap-1"
                  >
                    <ArchiveX className="h-3 w-3" /> Archive
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PacksTable({ packs, onPublish, onUnpublish }) {
  if (packs.length === 0) {
    return (
      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-12 text-center text-sm text-text-secondary">
        No packs yet. Trigger the monthly pack cron above.
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
      <table className="w-full text-xs" data-testid="admin-videos-packs-table">
        <thead className="bg-[var(--color-bg)] text-text-secondary">
          <tr>
            <th className="text-left px-4 py-3 font-medium uppercase tracking-widest">Pack</th>
            <th className="text-left px-4 py-3 font-medium uppercase tracking-widest">Creator</th>
            <th className="text-left px-4 py-3 font-medium uppercase tracking-widest">Month</th>
            <th className="text-right px-4 py-3 font-medium uppercase tracking-widest">Videos</th>
            <th className="text-left px-4 py-3 font-medium uppercase tracking-widest">Marketplace</th>
            <th className="text-right px-4 py-3 font-medium uppercase tracking-widest">Actions</th>
          </tr>
        </thead>
        <tbody>
          {packs.map((p) => (
            <tr
              key={p.pack_id}
              data-testid={`admin-videos-pack-row-${p.pack_id}`}
              className="border-t border-[var(--color-border)]"
            >
              <td className="px-4 py-3 text-[var(--color-text-main)]">{p.title || p.pack_id}</td>
              <td className="px-4 py-3 text-text-secondary">{p.virtual_creator_id}</td>
              <td className="px-4 py-3 text-text-secondary">{p.month_yyyy_mm}</td>
              <td className="px-4 py-3 text-right">{(p.video_ids || []).length}</td>
              <td className="px-4 py-3">
                {p.linked_marketplace_asset_id ? (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-[var(--color-success)]/40 bg-[var(--color-success)]/10 text-[var(--color-success)]">
                    Published
                  </span>
                ) : (
                  <span className="text-[10px] uppercase tracking-widest text-text-secondary">Unpublished</span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                {!p.linked_marketplace_asset_id ? (
                  <button
                    data-testid={`admin-videos-pack-publish-${p.pack_id}`}
                    onClick={() => onPublish(p.pack_id)}
                    className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-white hover:bg-[#EDEDED] text-black hover:opacity-90 inline-flex items-center gap-1"
                  >
                    <DollarSign className="h-3 w-3" /> Publish
                  </button>
                ) : (
                  <button
                    data-testid={`admin-videos-pack-unpublish-${p.pack_id}`}
                    onClick={() => onUnpublish(p.pack_id)}
                    className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-[var(--color-alert)]/40 text-[var(--color-alert)] hover:bg-[var(--color-alert)]/10"
                  >
                    Delist
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CountersHistory({ history }) {
  if (!history || history.length === 0) {
    return (
      <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-12 text-center text-sm text-text-secondary">
        No counters history.
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden">
      <table className="w-full text-xs" data-testid="admin-videos-counters-table">
        <thead className="bg-[var(--color-bg)] text-text-secondary">
          <tr>
            <th className="text-left px-4 py-3 font-medium uppercase tracking-widest">Date</th>
            <th className="text-right px-4 py-3 font-medium uppercase tracking-widest">Videos gen</th>
            <th className="text-right px-4 py-3 font-medium uppercase tracking-widest">Packs</th>
            <th className="text-right px-4 py-3 font-medium uppercase tracking-widest">LLM $</th>
          </tr>
        </thead>
        <tbody>
          {history.map((h) => (
            <tr
              key={h.date}
              data-testid={`admin-videos-counters-row-${h.date}`}
              className="border-t border-[var(--color-border)]"
            >
              <td className="px-4 py-3 text-[var(--color-text-main)]">{h.date}</td>
              <td className="px-4 py-3 text-right">{h.videos_generated || 0}</td>
              <td className="px-4 py-3 text-right">{h.packs_created || 0}</td>
              <td className="px-4 py-3 text-right">${(h.llm_usage_usd || 0).toFixed(3)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
